<template>
  <div class="pomodoro-stats" v-if="show">
    <div class="stats-overlay" @click="$emit('close')"></div>
    <div class="stats-panel">
      <div class="stats-header">
        <h2>{{ t('stats.title') }}</h2>
        <button class="close-btn" @click="$emit('close')">✕</button>
      </div>

      <div class="stats-content">
        <!-- 统计卡片 -->
        <div class="stats-cards">
          <div class="stat-card">
            <div class="stat-label">{{ t('stats.today') }}</div>
            <div class="stat-value">{{ stats.today.count }}</div>
            <div class="stat-sub">{{ formatDuration(stats.today.duration) }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">{{ t('stats.thisWeek') }}</div>
            <div class="stat-value">{{ stats.week.count }}</div>
            <div class="stat-sub">{{ formatDuration(stats.week.duration) }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">{{ t('stats.thisMonth') }}</div>
            <div class="stat-value">{{ stats.month.count }}</div>
            <div class="stat-sub">{{ formatDuration(stats.month.duration) }}</div>
          </div>
          <div class="stat-card">
            <div class="stat-label">{{ t('stats.total') }}</div>
            <div class="stat-value">{{ stats.total.count }}</div>
            <div class="stat-sub">{{ formatDuration(stats.total.duration) }}</div>
          </div>
        </div>

        <!-- 趋势图表 -->
        <div class="chart-block">
          <h3>{{ t('stats.chart') }}</h3>
          <div ref="chartRef" class="chart-box"></div>
        </div>

        <!-- 最近记录 -->
        <div class="recent-records">
          <h3>{{ t('pomodoro.title') }}</h3>
          <div class="records-list">
            <div v-if="recentRecords.length === 0" class="no-data">
              {{ t('stats.noData') }}
            </div>
            <div 
              v-for="record in recentRecords" 
              :key="record.id" 
              class="record-item"
            >
              <div class="record-time">{{ formatRecordTime(record.startTime) }}</div>
              <div class="record-duration">{{ record.duration }}min</div>
              <div class="record-note">{{ record.note || '-' }}</div>
            </div>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="stats-actions">
          <button class="action-btn export-btn" @click="handleExport">
            {{ t('common.export') }}
          </button>
          <button class="action-btn import-btn" @click="triggerImport">
            {{ t('common.import') }}
          </button>
          <input 
            ref="fileInput" 
            type="file" 
            accept=".json" 
            @change="handleImport" 
            style="display: none;"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref, onMounted, watch } from 'vue'
import { usePomodoro } from '../composables/usePomodoro'
import { useI18n } from '../composables/useI18n'

export default {
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const { stats, records, exportData, importData } = usePomodoro()
    const { t } = useI18n()
    const fileInput = ref(null)
    const chartRef = ref(null)
    let chart = null

    const recentRecords = computed(() => {
      return records.value
        .filter(r => r.completed)
        .sort((a, b) => new Date(b.startTime) - new Date(a.startTime))
        .slice(0, 10)
    })

    const formatDuration = (minutes) => {
      const hours = Math.floor(minutes / 60)
      const mins = minutes % 60
      if (hours > 0) {
        return `${hours}h ${mins}m`
      }
      return `${mins}m`
    }

    const formatRecordTime = (time) => {
      const date = new Date(time)
      return date.toLocaleString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const handleExport = () => {
      exportData()
    }

    const triggerImport = () => {
      fileInput.value.click()
    }

    const handleImport = async (event) => {
      const file = event.target.files[0]
      if (file) {
        try {
          await importData(file)
          alert(t('common.success') || '导入成功！')
          event.target.value = '' // 清空文件选择
        } catch (error) {
          alert(t('common.error') || '导入失败：' + error.message)
        }
      }
    }

    const buildLast7 = () => {
      const now = new Date()
      const days = []
      for (let i = 6; i >= 0; i--) {
        const d = new Date(now)
        d.setDate(now.getDate() - i)
        const key = d.toISOString().slice(0, 10)
        days.push({ key, label: `${d.getMonth()+1}/${d.getDate()}`, count: 0 })
      }
      const map = new Map(days.map(d => [d.key, d]))
      records.value.filter(r => r.completed).forEach(r => {
        const key = new Date(r.startTime).toISOString().slice(0,10)
        if (map.has(key)) map.get(key).count += 1
      })
      return days
    }

    const renderChart = async () => {
      if (!chartRef.value) {
        console.warn('Chart ref not available')
        return
      }
      try {
        if (!chart) {
          const echartsModule = await import(/* webpackChunkName: "echarts" */ 'echarts')
          const echarts = echartsModule.default || echartsModule
          chart = echarts.init(chartRef.value)
          console.log('✓ ECharts initialized')
        }
        const data = buildLast7()
        chart.setOption({
          tooltip: { trigger: 'axis' },
          xAxis: { 
            type: 'category', 
            data: data.map(d => d.label),
            axisLabel: { color: '#999' }
          },
          yAxis: { 
            type: 'value', 
            minInterval: 1,
            axisLabel: { color: '#999' }
          },
          textStyle: { color: '#fff' },
          grid: { containLabel: true },
          series: [{
            type: 'bar',
            data: data.map(d => d.count),
            itemStyle: { color: '#4CAF50' }
          }]
        })
        console.log('✓ Chart rendered with data:', data)
      } catch (err) {
        console.error('Chart rendering error:', err)
      }
    }

    onMounted(() => {
      renderChart()
    })

    watch(records, () => renderChart())

    return {
      stats,
      recentRecords,
      fileInput,
      chartRef,
      t,
      formatDuration,
      formatRecordTime,
      handleExport,
      triggerImport,
      handleImport
    }
  }
}
</script>

<style lang="less" scoped>
.pomodoro-stats {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
}

.stats-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
}

.stats-panel {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: #1a1a1a;
  border-radius: 12px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);

  @media (max-width: 768px) {
    width: 95%;
    max-height: 95vh;
  }
}

.stats-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #333;

  h2 {
    margin: 0;
    color: #fff;
    font-size: 24px;

    @media (max-width: 768px) {
      font-size: 20px;
    }
  }

  .close-btn {
    background: none;
    border: none;
    color: #999;
    font-size: 28px;
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      color: #fff;
    }
  }
}

.stats-content {
  padding: 20px;
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
}

.stat-card {
  background: #262626;
  padding: 20px;
  border-radius: 8px;
  text-align: center;

  .stat-label {
    color: #999;
    font-size: 14px;
    margin-bottom: 8px;
  }

  .stat-value {
    color: #fff;
    font-size: 32px;
    font-weight: bold;
    margin-bottom: 4px;
  }

  .stat-sub {
    color: #666;
    font-size: 12px;
  }
}

.recent-records {
  margin-bottom: 24px;

  h3 {
    color: #fff;
    margin: 0 0 16px 0;
    font-size: 18px;
  }

  .records-list {
    max-height: 300px;
    overflow-y: auto;
  }

  .no-data {
    text-align: center;
    color: #666;
    padding: 40px 20px;
  }

  .record-item {
    display: flex;
    align-items: center;
    padding: 12px;
    background: #262626;
    border-radius: 6px;
    margin-bottom: 8px;
    gap: 16px;

    @media (max-width: 768px) {
      flex-wrap: wrap;
      gap: 8px;
    }

    .record-time {
      color: #999;
      font-size: 14px;
      min-width: 100px;
    }

    .record-duration {
      color: #4CAF50;
      font-weight: bold;
      min-width: 60px;
    }

    .record-note {
      color: #ccc;
      flex: 1;
      font-size: 14px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.chart-block {
  margin-bottom: 24px;

  h3 {
    color: #fff;
    margin: 0 0 12px 0;
  }

  .chart-box {
    width: 100%;
    height: 240px;
    background: #1c1c1c;
    border-radius: 8px;
  }
}

.stats-actions {
  display: flex;
  gap: 12px;
  justify-content: center;

  @media (max-width: 768px) {
    flex-direction: column;
  }

  .action-btn {
    padding: 12px 24px;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.3s;

    @media (max-width: 768px) {
      width: 100%;
    }

    &.export-btn {
      background: #4CAF50;
      color: #fff;

      &:hover {
        background: #45a049;
      }
    }

    &.import-btn {
      background: #2196F3;
      color: #fff;

      &:hover {
        background: #0b7dda;
      }
    }
  }
}
</style>
